import Header from "@/atomic/atoms/Header";
import { PropsView } from "../Models/pin";
import Loading from "@/atomic/atoms/Loading";
import Button from "@/atomic/atoms/Button";
import Modal from "@/atomic/atoms/Modal";
import { Suspense, useCallback, useRef, useState } from "react";

const PIN_LENGTH = 5;

export default function RegisterPinView({
  timer,
  handleSubmit,
  isLoading,
  handleTryAgain,
  goBack,
  isDisabled,
  isVisibleModal,
  toggleModal,
  goToLogin,
  isEmail,
  isWhatsApp = false,
  register,
  setValue,
  errors,
  error,
}: PropsView) {
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [digits, setDigits] = useState<string[]>(() =>
    Array(PIN_LENGTH).fill("")
  );

  const hasFieldError = Boolean(errors.pin);
  const showError = hasFieldError || error;

  const commitPin = useCallback(
    (next: string[]) => {
      setDigits(next);
      setValue("pin", next.join(""), {
        shouldValidate: true,
        shouldTouch: true,
        shouldDirty: true,
      });
    },
    [setValue]
  );

  const focusAt = (index: number) => {
    const el = inputRefs.current[Math.max(0, Math.min(index, PIN_LENGTH - 1))];
    el?.focus();
    el?.select();
  };

  const handleChange = (index: number, raw: string) => {
    const d = raw.replace(/\D/g, "").slice(-1);
    const next = [...digits];
    next[index] = d;
    commitPin(next);
    if (d && index < PIN_LENGTH - 1) {
      focusAt(index + 1);
    }
  };

  const handleKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Backspace" && !digits[index] && index > 0) {
      e.preventDefault();
      focusAt(index - 1);
    }
    if (e.key === "ArrowLeft" && index > 0) {
      e.preventDefault();
      focusAt(index - 1);
    }
    if (e.key === "ArrowRight" && index < PIN_LENGTH - 1) {
      e.preventDefault();
      focusAt(index + 1);
    }
  };

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").replace(/\D/g, "").slice(0, PIN_LENGTH);
    if (!pasted) return;
    const next = Array(PIN_LENGTH)
      .fill("")
      .map((_, i) => pasted[i] ?? "");
    commitPin(next);
    focusAt(Math.min(pasted.length, PIN_LENGTH) - 1);
  };

  const tipText = isEmail
    ? "Se o código não chegar, verifique se o remetente do Sem Parar não está bloqueado."
    : isWhatsApp
      ? "Se o código não chegar, verifique se o contato do Sem Parar não está bloqueado."
      : "Se o código não chegar, verifique se o número do Sem Parar está bloqueado.";

  const timerLabel =
    timer !== 0
      ? `Reenviar código em 00:${timer < 10 ? "0" : ""}${timer}`
      : "Reenviar código";

  return (
    <div className="h-full relative flex flex-col bg-white overflow-hidden">
      <Header title="Dados pessoais" hasLeftButton goBack={goBack} />
      <main className="flex-1 w-full flex justify-center min-h-0 bg-background">
        <form
          className="w-full max-w-[480px] px-6 pt-6 pb-8 flex flex-col flex-1 min-h-0"
          onSubmit={handleSubmit}
        >
          <input type="hidden" {...register("pin")} />

          <div className="flex-1 min-h-0">
            <h2 className="text-text text-[22px] leading-tight font-primary font-semibold tracking-tight">
              Digite o código recebido
            </h2>
            <p className="mt-3 font-primary text-base text-tertiary leading-relaxed">
              Enviamos o código para o canal escolhido:
            </p>

            <div
              className="mt-8 flex justify-center gap-2 sm:gap-3"
              role="group"
              aria-label="Código de verificação de 5 dígitos"
            >
              {digits.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  onPaste={index === 0 ? handlePaste : undefined}
                  aria-invalid={showError}
                  className={[
                    "w-12 h-14 sm:w-14 sm:h-16 rounded-xl border text-center text-xl font-semibold font-primary text-text",
                    "focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary",
                    showError ? "border-error" : "border-border",
                  ].join(" ")}
                />
              ))}
            </div>

            {showError && (
              <div className="mt-4 flex items-start justify-center gap-1 px-1">
                <img
                  src="/icon-error.png"
                  width={16}
                  height={16}
                  alt=""
                  className="mt-0.5 shrink-0"
                />
                <p className="text-error text-center text-sm font-primary leading-snug">
                  {errors?.pin?.message ?? "Pin inválido. Tente novamente"}
                </p>
              </div>
            )}

            <p className="mt-6 font-primary text-sm text-tertiary leading-relaxed">
              {tipText}
            </p>
          </div>

          <div className="mt-10 shrink-0 flex flex-col gap-6">
            <div className="flex justify-center">
              <button
                type="button"
                disabled={timer !== 0}
                onClick={handleTryAgain}
                className={[
                  "font-primary text-base underline underline-offset-2 text-center",
                  timer !== 0
                    ? "text-tertiary cursor-not-allowed"
                    : "text-text active:opacity-80",
                ].join(" ")}
              >
                {timerLabel}
              </button>
            </div>
            <Button
              className="rounded-theme-large bg-primary text-white font-primary font-semibold"
              title="Continuar"
              type="submit"
              isDisabled={isDisabled}
            />
          </div>
        </form>
      </main>
      {isVisibleModal && (
        <Suspense fallback={null}>
          <Modal
            title="Atenção!"
            message="Já existe uma conta Pedágio Eletrônico Sem Parar para esse CPF. Clique em Acessar conta e faça login com CPF e senha."
            actionMessage="Acessar conta"
            action={() => {
              toggleModal();
              goToLogin();
            }}
            hasCloseButton={false}
          />
        </Suspense>
      )}
      {isLoading && (
        <Suspense fallback={null}>
          <Loading />
        </Suspense>
      )}
    </div>
  );
}
